/**
 * Часть аутентификации, идущая по HTTP, а не по WebSocket.
 *
 * Refresh-токен лежит в HttpOnly-cookie: браузер прикладывает её сам и только
 * к HTTP-запросам. Через WebSocket cookie не передать — это решение 1 из ROADMAP.
 *
 * Отсюда следует, что по HTTP идёт не только ротация, но и сам вход: cookie
 * ставит ответ сервера, а кадр сокета ответом HTTP не является. Вернуть
 * refresh-токен в теле кадра значило бы отдать JavaScript ключ, переживающий
 * закрытие вкладки, — то есть отменить HttpOnly, ради которого всё и затевалось.
 * Выход по той же причине: отозвать сессию может только тот, кто видит cookie.
 */

import { type ErrorBody, ErrorCode, HTTP_EQUIVALENT } from "@taskflow/contracts";

import { setAccessToken, type WsError } from "@taskflow/client-core";

import { apiUrl } from "@/shared/api/http";


/**
 * Основание HTTP-запросов. По умолчанию — тот же источник, откуда взята
 * страница: в продакшне фронтенд и API стоят за одним nginx. Задавать
 * адрес нужно только в разработке, где они на разных портах.
 *
 * Источник подставляется явно, а не остаётся пустой строкой: относительный
 * адрес понимает только браузер. В любом другом окружении — тестах,
 * предрендере — fetch("/auth/refresh") падает на разборе адреса ещё до
 * запроса, и отказ выглядит как мёртвая сессия.
 */
const HTTP_BASE = apiUrl("");

interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * Ротация идёт в один поток на всё приложение.
 *
 * Обновить токен могут двое: восстановление сессии при загрузке страницы
 * и WS-клиент, получивший SESSION_EXPIRED. Если они сделают это
 * одновременно, второй предъявит уже вытесненный токен — а сервер
 * обязан считать это кражей и погасить ВСЕ сессии человека (§4.2).
 * То есть обычная перезагрузка выбрасывала бы его со всех устройств.
 *
 * Защиты внутри WS-клиента мало: она разводит только его собственные
 * запросы. Одиночность обязана быть там, где происходит сама ротация.
 */
let inFlight: Promise<boolean> | null = null;

/**
 * Обменивает refresh-cookie на новый access-токен.
 * Возвращает false, если сессия мертва — вызывающий обязан увести на /login.
 */
export function refreshAccessToken(): Promise<boolean> {
  inFlight ??= rotate().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function rotate(): Promise<boolean> {
  try {
    const response = await fetch(`${HTTP_BASE}/auth/refresh`, {
      method: "POST",
      // Без credentials браузер не пошлёт cookie на другой источник.
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      setAccessToken(null);
      return false;
    }

    const data = (await response.json()) as RefreshResponse;
    setAccessToken(data.accessToken);
    return true;
  } catch (error) {
    // Сеть недоступна — это не «сессия мертва», но восстановиться сейчас нельзя.
    console.error("[auth] refresh не удался", error);
    setAccessToken(null);
    return false;
  }
}

/** Ответ сервера об ошибке: `{ "error": { code, message, … } }` (§21.2). */
interface HttpErrorEnvelope {
  error?: ErrorBody;
}

function toWsError(status: number, body: unknown): WsError {
  const parsed = (body as HttpErrorEnvelope | null)?.error;
  if (parsed) {
    return { status, code: parsed.code, data: parsed };
  }
  // Ответ не в формате §21.2 — прокси или падение до приложения.
  // Подменять код на «неверный пароль» нельзя: это разные причины.
  const data: ErrorBody = {
    code: ErrorCode.INTERNAL_ERROR,
    message: "Сервер недоступен. Повторите позже",
    httpEquivalent: HTTP_EQUIVALENT[ErrorCode.INTERNAL_ERROR],
    correlationId: "client",
  };
  return { status, code: data.code, data };
}

/**
 * Вход. Access-токен возвращается в теле и кладётся в память; refresh-токен
 * сервер ставит cookie сам — клиент его не видит и видеть не должен.
 */
export async function httpLogin<T>(
  credentials: { email: string; password: string },
): Promise<{ data: T } | { error: WsError }> {
  return post<T>("/auth/login", credentials);
}

async function post<T>(
  path: string,
  body: unknown,
): Promise<{ data: T } | { error: WsError }> {
  return send<T>("POST", path, body);
}

async function send<T>(
  method: "GET" | "POST",
  path: string,
  body?: unknown,
): Promise<{ data: T } | { error: WsError }> {
  let response: Response;
  try {
    response = await fetch(`${HTTP_BASE}${path}`, {
      method,
      // Без credentials браузер не примет и не пошлёт refresh-cookie.
      credentials: "include",
      ...(body === undefined
        ? {}
        : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
  } catch {
    // Сеть недоступна. Это не «неверный пароль», и показывать надо разное.
    const data: ErrorBody = {
      code: ErrorCode.INTERNAL_ERROR,
      message: "Сервер недоступен. Проверьте соединение и повторите",
      httpEquivalent: HTTP_EQUIVALENT[ErrorCode.INTERNAL_ERROR],
      correlationId: "client",
    };
    return { error: { status: data.httpEquivalent, code: data.code, data } };
  }

  const parsed: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: toWsError(response.status, parsed) };
  }
  return { data: parsed as T };
}

/**
 * Приём приглашения. Тоже по HTTP: приём заводит сессию, а её refresh-токен
 * обязан лечь в HttpOnly-cookie — см. комментарий к модулю.
 */
export async function httpAcceptInvitation<T>(
  token: string,
  profile: {
    password?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    position?: string;
  },
): Promise<{ data: T } | { error: WsError }> {
  return post<T>(`/invitations/${encodeURIComponent(token)}/accept`, profile);
}

// ── Гостевые запросы ────────────────────────────────────────────────────
// У гостя нет access-токена, а WebSocket без него соединение не держит:
// прежде страница приглашения и «Забыли пароль?» не получали ответа вовсе.

/** Данные приглашения для страницы по ссылке из письма (§4.4). */
export async function httpInvitation<T>(token: string): Promise<{ data: T } | { error: WsError }> {
  return send<T>("GET", `/invitations/${encodeURIComponent(token)}`);
}

export async function httpDeclineInvitation<T>(
  token: string,
): Promise<{ data: T } | { error: WsError }> {
  return post<T>(`/invitations/${encodeURIComponent(token)}/decline`, {});
}

export async function httpRequestPasswordReset<T>(
  email: string,
): Promise<{ data: T } | { error: WsError }> {
  return post<T>("/auth/password-reset/request", { email });
}

export async function httpConfirmPasswordReset<T>(
  token: string,
  password: string,
): Promise<{ data: T } | { error: WsError }> {
  return post<T>("/auth/password-reset/confirm", { token, password });
}

/** Выход: сервер отзывает предъявленную сессию и стирает cookie. */
export async function httpLogout(): Promise<void> {
  try {
    await fetch(`${HTTP_BASE}/auth/logout`, { method: "POST", credentials: "include" });
  } catch (error) {
    // Даже если запрос не дошёл, локальную сессию всё равно гасим.
    console.error("[auth] logout не дошёл до сервера", error);
  } finally {
    setAccessToken(null);
  }
}
